"use client";

import { useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/features/auth/hook/useAuth";
import { AuthError } from "@/components/shared/AuthError";

const registerSchema = z.object({
    name: z.string()
        .min(2, "Nom trop court")
        .max(50, "Nom trop long")
        .regex(/^[a-zA-ZÀ-ÿ\s'-]+$/, "Nom invalide")
        .min(1, "Nom obligatoire"),
    phone: z.string()
        .regex(/^[0-9]{8,15}$/, "Numéro invalide")
        .min(1, "Téléphone obligatoire"),
    email: z.string()
        .email("Email invalide")
        .min(1, "Email obligatoire"),
    password: z.string()
        .min(8, "Minimum 8 caractères")
        .regex(/[A-Z]/, "Au moins une majuscule")
        .regex(/[a-z]/, "Au moins une minuscule")
        .regex(/[0-9]/, "Au moins un chiffre")
        .regex(/[^A-Za-z0-9]/, "Au moins un caractère spécial")
        .min(1, "Mot de passe obligatoire"),
    confirmPassword: z.string()
        .min(1, "Confirmation obligatoire"),
}).refine((data) => data.password === data.confirmPassword, {
    message: "Les mots de passe ne correspondent pas",
    path: ["confirmPassword"],
});

type RegisterFormValues = z.infer<typeof registerSchema>;

export const RegisterForm = () => {
    const router = useRouter();
    const { register: registerUser, isLoading, error } = useAuth();
    const [showPassword, setShowPassword] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<RegisterFormValues>({
        resolver: zodResolver(registerSchema),
        defaultValues: {
            name: "",
            phone: "",
            email: "",
            password: "",
            confirmPassword: "",
        },
    });

    const onSubmit = (values: RegisterFormValues) => {
        registerUser(
            {
                name: values.name,
                phone: values.phone,
                email: values.email,
                password: values.password,
                role: "CLIENT",
            },
            {
                onSuccess: () => {
                    router.push("/auth/login");
                },
            }
        );
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">

            {/* Name */}
            <div>
                <Input
                    {...register("name")}
                    placeholder="Nom complet"
                />
                {errors.name && (
                    <p className="text-sm text-red-500 mt-1">
                        {errors.name.message}
                    </p>
                )}
            </div>

            {/* Phone */}
            <div>
                <Input
                    {...register("phone")}
                    placeholder="Numéro de téléphone"
                />
                {errors.phone && (
                    <p className="text-sm text-red-500 mt-1">
                        {errors.phone.message}
                    </p>
                )}
            </div>

            {/* Email */}
            <div>
                <Input
                    {...register("email")}
                    type="email"
                    placeholder="Email"
                />
                {errors.email && (
                    <p className="text-sm text-red-500 mt-1">
                        {errors.email.message}
                    </p>
                )}
            </div>

            {/* Password */}
            <div className="relative">
                <Input
                    {...register("password")}
                    type={showPassword ? "text" : "password"}
                    placeholder="Mot de passe"
                    className="pr-10"
                />
                <button
                    type="button"
                    className="absolute right-3 top-3 text-gray-400"
                    onClick={() => setShowPassword(!showPassword)}
                >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>

                {errors.password && (
                    <p className="text-sm text-red-500 mt-1">
                        {errors.password.message}
                    </p>
                )}
            </div>

            {/* Confirm Password */}
            <div>
                <Input
                    {...register("confirmPassword")}
                    type="password"
                    placeholder="Confirmer le mot de passe"
                />
                {errors.confirmPassword && (
                    <p className="text-sm text-red-500 mt-1">
                        {errors.confirmPassword.message}
                    </p>
                )}
            </div>

            {/* Terms */}
            <div className="flex items-start gap-2 text-sm">
                <input type="checkbox" required />
                <span>
                    En vous inscrivant, vous acceptez nos{" "}
                    <span className="text-primary-600 font-medium">
                        Conditions Générales
                    </span>
                </span>
            </div>

            {/* Backend Error */}
            <AuthError message={error as string} />

            {/* Submit */}
            <Button type="submit" disabled={isLoading} className="w-full">
                {isLoading ? "Inscription..." : "S'inscrire"}
            </Button>

            {/* Login */}
            <p className="text-center text-sm text-gray-500">
                Vous avez déjà un compte ?{" "}
                <Link
                    href="/auth/login"
                    className="text-primary-600 font-medium"
                >
                    Se connecter
                </Link>
            </p>
        </form>
    );
};

